"use client"

import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"

/**
 * Meta buys on impressions, so a Facebook budget model that starts from CPM and
 * walks the funnel down is closer to how the auction actually spends than the
 * generic "customers x CAC" shape in ``ad-budget-widget``.
 *
 * Deliberately takes CPM / CTR / CVR from the operator rather than shipping
 * baked-in industry benchmarks: a benchmark we cannot source from Meta is a
 * number this page has no business asserting.
 */
export function FacebookAdsBudgetWidget() {
  // Forward: budget -> results
  const [budget, setBudget] = useState("5000")
  const [cpm, setCpm] = useState("12")
  const [ctr, setCtr] = useState("1.2")
  const [cvr, setCvr] = useState("3")
  // Reverse: results -> budget
  const [targetResults, setTargetResults] = useState("200")
  const [rCpm, setRCpm] = useState("12")
  const [rCtr, setRCtr] = useState("1.2")
  const [rCvr, setRCvr] = useState("3")

  const forward = (() => {
    const b = parseNum(budget)
    const m = parseNum(cpm)
    const t = parseNum(ctr)
    const v = parseNum(cvr)
    if (b === null || m === null || t === null || v === null || m <= 0) return null
    const impressions = (b / m) * 1000
    const clicks = impressions * (t / 100)
    const results = clicks * (v / 100)
    return { impressions, clicks, results, cpa: results > 0 ? b / results : NaN }
  })()

  const reverse = (() => {
    const r = parseNum(targetResults)
    const m = parseNum(rCpm)
    const t = parseNum(rCtr)
    const v = parseNum(rCvr)
    if (r === null || m === null || t === null || v === null) return null
    if (t <= 0 || v <= 0) return null
    const clicks = r / (v / 100)
    const impressions = clicks / (t / 100)
    const spend = (impressions / 1000) * m
    return { clicks, impressions, spend, cpa: r > 0 ? spend / r : NaN }
  })()

  const int = (n: number) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : "—")

  return (
    <CalculatorCard title="Facebook Ads Budget Calculator">
      <Tabs defaultValue="forward">
        <TabsList className="mb-6">
          <TabsTrigger value="forward">Budget → results</TabsTrigger>
          <TabsTrigger value="reverse">Results → budget</TabsTrigger>
        </TabsList>

        <TabsContent value="forward">
          <CalculatorGrid>
            <NumberField id="fb-budget" label="Monthly budget" value={budget} onChange={setBudget} prefix="$" />
            <NumberField id="fb-cpm" label="Expected CPM" value={cpm} onChange={setCpm} prefix="$" />
            <NumberField id="fb-ctr" label="Expected CTR" value={ctr} onChange={setCtr} suffix="%" step={0.1} />
            <NumberField id="fb-cvr" label="Landing page conversion rate" value={cvr} onChange={setCvr} suffix="%" step={0.1} />
          </CalculatorGrid>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <ResultStat label="Impressions" value={int(forward?.impressions ?? NaN)} />
            <ResultStat label="Clicks" value={int(forward?.clicks ?? NaN)} />
            <ResultStat label="Conversions" value={int(forward?.results ?? NaN)} />
            <ResultStat
              label="Implied CPA"
              value={forward ? formatCurrency(forward.cpa) : "—"}
              caption={
                forward && Number.isFinite(forward.cpa)
                  ? "Compare this against what one conversion is worth before committing the budget."
                  : "Enter a non-zero CPM, CTR and conversion rate."
              }
            />
          </div>
          <FormulaNote>
            Impressions = (Budget ÷ CPM) × 1,000 · Clicks = Impressions × CTR · Conversions = Clicks × conversion rate.
          </FormulaNote>
        </TabsContent>

        <TabsContent value="reverse">
          <CalculatorGrid>
            <NumberField id="fb-r-results" label="Conversions needed" value={targetResults} onChange={setTargetResults} min={0} step={1} />
            <NumberField id="fb-r-cpm" label="Expected CPM" value={rCpm} onChange={setRCpm} prefix="$" />
            <NumberField id="fb-r-ctr" label="Expected CTR" value={rCtr} onChange={setRCtr} suffix="%" step={0.1} />
            <NumberField id="fb-r-cvr" label="Landing page conversion rate" value={rCvr} onChange={setRCvr} suffix="%" step={0.1} />
          </CalculatorGrid>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <ResultStat label="Clicks required" value={int(reverse?.clicks ?? NaN)} />
            <ResultStat label="Impressions required" value={int(reverse?.impressions ?? NaN)} />
            <ResultStat
              label="Budget required"
              value={reverse ? formatCurrency(reverse.spend) : "—"}
              caption={
                reverse
                  ? "The spend needed to buy that many impressions at your CPM."
                  : "Enter a non-zero CTR and conversion rate."
              }
            />
            <ResultStat label="Implied CPA" value={reverse ? formatCurrency(reverse.cpa) : "—"} />
          </div>
          <FormulaNote>
            Clicks = Conversions ÷ conversion rate · Impressions = Clicks ÷ CTR · Budget = (Impressions ÷ 1,000) × CPM.
          </FormulaNote>
        </TabsContent>
      </Tabs>
    </CalculatorCard>
  )
}
